/**
 * The deep-link params the registration wizard reads off its own URL.
 *
 * A link can name the division and team the reader is registering into, and
 * lock the wizard to one role. The wizard cannot infer any of the three, so
 * they travel in the query string and are pre-selected on arrival.
 *
 * Kept in `common/` rather than beside the wizard because the links are built
 * elsewhere — today by hand, and by whatever surface later lists open spots.
 * A builder and a reader that disagree on a param name send the reader to a
 * different team than the row they clicked, so both sides take the name from
 * here.
 */

/** Query param carrying the team id into the registration wizard. */
export const REGISTRATION_TEAM_PARAM = 'team_id'

/** Query param carrying the division id into the wizard. */
export const REGISTRATION_DIVISION_PARAM = 'division_id'

/** Query param locking the wizard to one registration role. */
export const REGISTRATION_ROLE_PARAM = 'type'

/**
 * Query param carrying a Team Budget invite (Jeff, Oct 1): the invitation's own
 * token, which holds a slot on the team for the person it was sent to.
 *
 * Read once, when the wizard opens a session — the server binds the invite to
 * the new session, and from then on the session holds it. Never carried from
 * step to step (`preserveRegistrationParams` leaves it out), and never built by
 * the client: the invite email's link is the only place one comes from.
 */
export const REGISTRATION_INVITE_PARAM = 'invite'

/** Accepted `type` values. */
export const REGISTRATION_ROLE_PLAYER = 'player'
export const REGISTRATION_ROLE_TEAM = 'team'

export interface RegistrationUrlParams {
  /** Pre-selects the team on the register step. */
  teamId?: string
  /**
   * Pre-selects the division on step one. Also what makes the team pre-select
   * reachable: the team list is fetched per division, so a reader left on the
   * wrong division would never see the team.
   */
  divisionId?: string
  /** Locks the wizard to this role, disabling the other. */
  role?: typeof REGISTRATION_ROLE_PLAYER | typeof REGISTRATION_ROLE_TEAM
}

/**
 * Build a registration deep link.
 *
 * Every part is optional: a caller may know only the division, and a
 * `team_id=` with nothing after it would be a param the wizard has to defend
 * against. Omit them instead.
 */
export const buildRegistrationUrl = (
  scheduleId: string,
  { teamId, divisionId, role }: RegistrationUrlParams = {}
): string => {
  const params = new URLSearchParams()
  if (divisionId) {
    params.set(REGISTRATION_DIVISION_PARAM, divisionId)
  }
  if (teamId) {
    params.set(REGISTRATION_TEAM_PARAM, teamId)
  }
  if (role) {
    params.set(REGISTRATION_ROLE_PARAM, role)
  }
  const qs = params.toString()
  return qs
    ? `/registration/${scheduleId}?${qs}`
    : `/registration/${scheduleId}`
}
